function formatMoney(cents, format = '${{amount}}') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  let value = '';
  let placeholderRegex = /\{\{\s*(\w+)\s*}}/;

  function formatWithDelimiters(number, decimals = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number === null) return 0;

    number = (number / 100.0).toFixed(decimals);

    let parts = number.split('.');
    let dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    let cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  let formatType = format.match(placeholderRegex)[1];

  switch (formatType) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return format.replace(placeholderRegex, value);
}

const requestIdleCallback = typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

class UpsellBlock {
  constructor(container) {
    if (!container) return;

    // Elements
    this.container = container;
    this.sectionId = this.container.dataset.sectionId;
    this.sectionType = this.container.dataset.typeSection;
    this.productsContainer = document.getElementById(`UpsellProducts-${this.sectionId}`);
    this.dotsContainer = document.getElementById(`UpsellDots-${this.sectionId}`);

    // Settings
    this.isSlider = this.productsContainer.classList.contains('upsell-slider');
    this.productItems = Array.from(this.productsContainer.querySelectorAll('.upsell-product-item'));
    this.totalProducts = this.productItems.length;

    // Set visible slides to 1.2 (showing part of next slide)
    this.visibleSlides = 1.1;

    // Slider state
    this.currentSlide = 0;
    this.totalSlides = Math.ceil(this.totalProducts - (this.visibleSlides - 1));
    this.slideWidth = 100 / this.visibleSlides;
    this.dragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    // Initialize
    this.init();

    this.productLogic = {
      productsCart: [],
      products: [],
      minProducts: 3,
    };

    if (!!this.sectionType && this.sectionType == 'cart') {
      this.getProductsByFirstElementOnCart();
    }
  }

  init() {
    if (!this.isSlider) return;

    this.setupSlider();

    // Create navigation elements
    this.createNavigation();

    // Set up event listeners
    this.setupEventListeners();

    // Add to cart buttons event listeners
    this.initAddToCartButtons();
  }

  setupSlider() {
    // Set width for each product item based on visibleSlides
    this.productItems.forEach(item => {
      item.style.flex = `0 0 ${this.slideWidth}%`;
      item.style.maxWidth = `${this.slideWidth}%`;
    });

    // Adjust container to accommodate fractional slides
    this.productsContainer.style.setProperty('--slide-width', `${this.slideWidth}%`);
  }

  createNavigation() {
    // Create dots if needed
    if (this.dotsContainer) {
      this.createDots();
    }
  }

  createDots() {
    // Clear existing dots
    this.dotsContainer.innerHTML = '';

    // Create dots based on total slides
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('div');
      dot.classList.add('upsell-dot');
      if (i === 0) dot.classList.add('active');
      dot.dataset.slideIndex = i;
      dot.addEventListener('click', e => this.handleDotClick(e));
      this.dotsContainer.appendChild(dot);
    }
  }

  setupEventListeners() {
    // Touch events for mobile
    this.productsContainer.addEventListener('touchstart', e => this.touchStart(e));
    this.productsContainer.addEventListener('touchmove', e => this.touchMove(e));
    this.productsContainer.addEventListener('touchend', () => this.touchEnd());

    // Mouse events for desktop drag
    this.productsContainer.addEventListener('mousedown', e => this.touchStart(e));
    this.productsContainer.addEventListener('mousemove', e => this.touchMove(e));
    this.productsContainer.addEventListener('mouseup', () => this.touchEnd());
    this.productsContainer.addEventListener('mouseleave', () => this.touchEnd());

    // Prevent context menu on long press
    this.productsContainer.addEventListener('contextmenu', e => {
      if (this.dragging) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Prevent image dragging
    this.productItems.forEach(item => {
      const image = item.querySelector('img');
      if (image) {
        image.addEventListener('dragstart', e => e.preventDefault());
      }
    });

    // Window resize event
    window.addEventListener('resize', () => this.handleResize());

    if (this.totalProducts) {
      this.productsContainer.classList.add('upsell-one-product');
    }
  }

  touchStart(event) {
    this.dragging = true;
    this.startPos = this.getPositionX(event);
    this.productsContainer.style.transition = 'none';
    this.productsContainer.style.cursor = 'grabbing';
  }

  touchMove(event) {
    if (!this.dragging) return;

    const currentPosition = this.getPositionX(event);
    let moveDistance = currentPosition - this.startPos;

    if (event.type.includes('mouse')) {
      moveDistance *= 0.5;
    }

    this.currentTranslate = this.prevTranslate + moveDistance;

    const maxTranslate = 0;
    const minTranslate = -(this.totalSlides - 1) * this.slideWidth;

    if (this.currentTranslate > maxTranslate) {
      this.currentTranslate = maxTranslate + (this.currentTranslate - maxTranslate) * 0.3;
    } else if (this.currentTranslate < minTranslate) {
      this.currentTranslate = minTranslate + (this.currentTranslate - minTranslate) * 0.3;
    }

    this.setSliderPosition(this.currentTranslate);
  }

  touchEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    this.productsContainer.style.cursor = 'grab';

    const threshold = this.slideWidth * 0.2;
    const moveDistance = this.currentTranslate - this.prevTranslate;

    if (moveDistance < -threshold) {
      this.nextSlide();
    } else if (moveDistance > threshold) {
      this.prevSlide();
    } else {
      this.goToSlide(this.currentSlide);
    }

    this.productsContainer.style.transition = 'transform 0.3s ease';
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  handleDotClick(event) {
    const slideIndex = parseInt(event.target.dataset.slideIndex);
    this.goToSlide(slideIndex);
  }

  goToSlide(slideIndex) {
    if (slideIndex < 0) {
      slideIndex = 0;
    } else if (slideIndex >= this.totalSlides) {
      slideIndex = this.totalSlides - 1;
    }

    this.currentSlide = slideIndex;
    this.prevTranslate = -slideIndex * this.slideWidth;
    this.currentTranslate = this.prevTranslate;
    this.setSliderPosition(this.currentTranslate);
    this.updateActiveDot();

    this.productsContainer.style.transition = 'transform 0.3s ease';
  }

  setSliderPosition(position) {
    this.productsContainer.style.transform = `translateX(${position}%)`;
  }

  updateActiveDot() {
    if (!this.dotsContainer) return;

    const dots = this.dotsContainer.querySelectorAll('.upsell-dot');
    dots.forEach((dot, index) => {
      if (index === this.currentSlide) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  nextSlide() {
    this.goToSlide(this.currentSlide + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentSlide - 1);
  }

  handleResize() {
    this.goToSlide(this.currentSlide);
  }

  initAddToCartButtons() {
    const addToCartButtons = this.container.querySelectorAll('.upsell-add-to-cart');

    addToCartButtons.forEach(button => {
      button.addEventListener('click', event => {
        const handle = button.getAttribute('data-handle');
        if (!handle) return;
        const modal = new ModalUpsell('moda-upsell-add-to-cart');
        modal.fetchContent(`/products/${handle}`);
      });
    });
  }

  getProductsByFirstElementOnCart() {
    // Fetch the cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Check if the cart has items
        if (!cart.items || cart.items.length === 0) return;

        // Get the first item from the cart
        const firstItem = cart.items[0];
        const handle = firstItem.handle;

        if (!handle) {
          console.log('No handle found for the first cart item');
          return;
        }

        fetch(`/products/${handle}`)
          .then(response => response.text())
          .then(html => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            const upsellDataScript = tempDiv.querySelector('.upsell-data');

            if (!upsellDataScript) {
              this.fetchProductsFromSectionAPI(handle);
              return;
            }

            try {
              const upsellData = JSON.parse(upsellDataScript.textContent);
              const products = upsellData.has_metafield_products ?? upsellData.has_fallback_products;

              if (!upsellData || !products || products.length === 0) {
                console.log('No upsell products found');
                return;
              }

              this.updateProductsLogic(upsellData, cart);
            } catch (error) {
              console.error('Error parsing upsell data:', error);

              this.fetchProductsFromSectionAPI(handle);
            }
          })
          .catch(error => {
            console.error('Error fetching product page:', error);
          });
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  }

  updateProductsLogic(upsellData, cart) {
    const availableProducts = upsellData.has_metafield_products ?? upsellData.has_fallback_products ?? [];

    const cartProductIds = cart.items.map(item => item.id);
    const filteredProducts = availableProducts.filter(product => !cartProductIds.includes(product.id));

    if (filteredProducts.length < 3) {
      console.warn('Cannot display upsell: Need at least 3 products not in cart');
    }

    const selectedProducts = filteredProducts.slice(0, 3);

    this.productLogic = {
      productsCart: selectedProducts,
      ...this.productLogic,
    };

    this.updateProductsHTML(selectedProducts);
  }

  fetchProductsFromSectionAPI(handle) {
    // Use Shopify Section API as fallback
    fetch(`/products/${handle}?section_id=product-recommendations`)
      .then(response => response.text())
      .then(html => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const productItems = tempDiv.querySelectorAll('.upsell-product-item');

        if (productItems && productItems.length > 0) {
          this.productsContainer.innerHTML = '';

          productItems.forEach(item => {
            this.productsContainer.appendChild(item.cloneNode(true));
          });

          this.reinitSlider();
        }
      })
      .catch(error => {
        console.error('Error fetching section data:', error);
      });
  }

  updateProductsHTML(products) {
    // Clear current products container
    this.productsContainer.innerHTML = '';

    // Generate HTML for each product
    products.forEach(product => {
      if (!product.handle) return;

      const variantId = product.variants && product.variants.length > 0 ? product.variants[0].id : '';
      const price = product.variants && product.variants.length > 0 ? product.variants[0].price : product.price;
      const formattedPrice = typeof price === 'number' ? `${formatMoney(price, (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}')}` : price;
      const formattedDiscountedPrice = typeof price === 'number' ? `${formatMoney(price - 1000, (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}')}` : price;
      const imageUrl = product.featured_image || (product.images && product.images.length > 0 ? product.images[0] : '');

      const productHTML = `
        <div class="upsell-product-item" style="max-width: ${this.slideWidth}%; flex: 0 0 ${this.slideWidth}%;" data-product-id="${product.id}">
          <div class="upsell-product-image">
            <a href="/products/${product.handle}">
              <img src="${imageUrl}" alt="${product.title}" loading="lazy" width="300" height="300">
            </a>
          </div>
          <div class="upsell-product-info">
            <h3 class="upsell-product-title">
              <a href="/products/${product.handle}">${product.title}</a>
            </h3>
            <div class="upsell-product-price">
              <span class="upsell-price-sale">From ${formattedDiscountedPrice}</span>
              <span class="upsell-price-compare">${formattedPrice}</span>

            </div>
            <div class="upsell-product-actions">
              <button type="button" class="upsell-add-to-cart" data-product-id="${product.id}" data-variant-id="${variantId}" data-handle="${product.handle}">
                + Add
              </button>
            </div>
          </div>
        </div>
      `;

      this.productsContainer.innerHTML += productHTML;
    });

    // Reinitialize the slider
    this.reinitSlider();
  }

  reinitSlider() {
    // Update product items
    this.productItems = Array.from(this.productsContainer.querySelectorAll('.upsell-product-item'));
    this.totalProducts = this.productItems.length;

    // Recalculate slider properties
    this.totalSlides = Math.ceil(this.totalProducts - (this.visibleSlides - 1));
    this.currentSlide = 0;
    this.prevTranslate = 0;
    this.currentTranslate = 0;

    // Reset slider position
    this.setSliderPosition(0);

    // Reset navigation
    if (this.dotsContainer) {
      this.createDots();
    }

    // Set up event listeners for new elements
    this.setupEventListeners();

    // Initialize add to cart buttons
    this.initAddToCartButtons();

    if (this.totalProducts <= 1) {
      this.productsContainer.classList.add('upsell-one-product');
    } else {
      this.productsContainer.classList.remove('upsell-one-product');
    }
  }
}

// Initialize all upsell blocks when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const upsellContainers = document.querySelectorAll('[id^="UpsellContainer-"]');

  if (upsellContainers.length > 0) {
    upsellContainers.forEach(container => {
      new UpsellBlock(container);
    });
  }
});

class ModalUpsell {
  constructor(modalId) {
    this.modal = this.createModal(modalId);
    this.qty = 1;
    this.currentVariant = {};
    this.QTY_SELECTOR = [1, 2, 3, 4];

    requestIdleCallback(() => {
      document.documentElement.classList.add('js-drawer-open');
      document.body.appendChild(this.modal);
    });
  }

  createModal(modalId) {
    const existingModal = document.querySelector('.modal-upsell-main');
    if (existingModal) {
      existingModal.remove();
    }

    const modal = document.createElement('div');
    modal.id = modalId;
    modal.classList.add('cs-modal', 'modal-upsell-main');
    modal.tabIndex = -1;
    modal.role = 'dialog';
    modal.ariaModal = 'true';
    modal.innerHTML = `
      <div class="cs-modal-dialog cs-modal-dialog-scrollable">
        <div class="cs-modal-content">
                  <button type="button" class="modal-close" aria-label="Close">
          <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M6.63593 4.94978L11.5857 0L12.9999 1.41421L8.05013 6.36398L12.9999 11.3137L11.5857 12.7279L6.63593 7.77818L1.68619 12.7279L0.271973 11.3137L5.22173 6.36398L0.271973 1.41421L1.68619 0L6.63593 4.94978Z" fill="#333029"/>
          </svg>
      </button>

          <div class="cs-modal-body">

          <div class="modal-loading-state">
          <img src="https://cdn.shopify.com/s/files/1/0538/5931/9979/files/loading.png?v=1742836841" alt="loading state image" class="loading-state-image" />
          </div>
          <div class="modal-product"></div>
          <div class="modal-gallery"></div>
          <div class="modal-content lola-modal-content"></div>

          </div>
                <div class="modal-footer cs-modal-footer">
          <button type="button" class="button w-full mb-16 btn cs-add-to-cart custom-black-btn btn--full add-to-cart">${(window.theme && window.theme.strings && window.theme.strings.addToCart) || 'Add to cart'}</button>
          <a href="#" class="button w-full text-center">${(window.theme && window.theme.strings && window.theme.strings.details) || 'Details'}</a>
          </div>
        </div>
      </div>
    `;

    modal.querySelector('.modal-close').addEventListener('click', () => this.close());
    return modal;
  }

  async fetchContent(url) {
    this.url = url || '';

    let localePrefix = '';
    const currentPath = window.location.pathname;
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})\//);
    if (localeMatch) {
      localePrefix = '/' + localeMatch[1] + '/';
    } else if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root !== '/') {
      localePrefix = window.Shopify.routes.root;
    }

    const fullUrl = localePrefix && !url.startsWith(localePrefix) ? localePrefix + (url.startsWith('/') ? url.slice(1) : url) : url;

    try {
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error('Error al obtener el contenido');
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const contentTitle = doc.querySelector('.product-single__header');
      const settings = JSON.parse(doc.querySelector('[data-upsell-content]').innerHTML);

      this.settings = settings;
      this.currentVariant = settings.current_variant;

      const actionSelector = this.modal.querySelector('.modal-content');
      const actionProduct = this.modal.querySelector('.modal-product');
      const actionGallery = this.modal.querySelector('.modal-gallery');
      const addToCartButton = this.modal.querySelector('.modal-footer button');

      const loadingHMTL = this.modal.querySelector('.modal-loading-state');
      loadingHMTL.remove();

      if (contentTitle) {
        actionProduct.innerHTML += contentTitle.innerHTML;
      }

      actionGallery.innerHTML += this.generateGallery();
      this.validateIsAvailable(addToCartButton);
      this.initGallery();
      this.filterGalleryBySize(this.currentVariant);

      actionSelector.innerHTML += this.generateVariants();
      actionSelector.innerHTML += this.generateQtySelector();
      this.initVariantSelectors();
      this.updateProductPrice(this.currentVariant);
      this.iniQtySelector();
      this.assignAddToCartButton();
      this.updateBottomCTA();
      requestAnimationFrame(() => this.open());
    } catch (error) {
      console.error(error);
    }
  }

  filterGalleryBySize(variant) {
    const gallery = this.modal.querySelector('.product-gallery .gallery-container');
    if (!gallery || !variant?.options?.length) return;

    const selectedSize = variant.options.find(option => typeof option === 'string').toLowerCase();
    const slides = [...gallery.querySelectorAll('.gallery-slide')];
    const filtered = [];

    slides.forEach(slide => {
      const alt = slide.querySelector('img')?.alt || '';
      const sizeMatch = alt.includes('bundle-filtered-image:') ? alt.split('bundle-filtered-image:')[1].trim().toLowerCase() : null;

      if (!sizeMatch || sizeMatch === selectedSize) {
        slide.classList.add('is-visible');
        slide.classList.remove('is-hidden');
        filtered.push(slide);
      } else if (sizeMatch) {
        slide.classList.remove('is-visible');
        slide.classList.add('is-hidden');
      } else {
        slide.classList.remove('is-visible');
        slide.classList.remove('is-hidden');
      }
    });

    gallery.scrollLeft = 0;
    this.updateGalleryDots();
  }

  updateGalleryDots() {
    const gallery = this.modal.querySelector('.product-gallery .gallery-container');
    const bulletsContainer = this.modal.querySelector('.gallery-pagination');
    if (!gallery || !bulletsContainer) return;

    const visibleSlides = [...gallery.querySelectorAll('.gallery-slide.is-visible')];
    bulletsContainer.innerHTML = '';

    visibleSlides.forEach((slide, index) => {
      const dot = document.createElement('span');
      dot.classList.add('gallery-bullet');
      dot.dataset.index = index;
      if (index === 0) dot.classList.add('active');
      dot.addEventListener('click', () => {
        const slideWidth = slide.offsetWidth;

        bulletsContainer.querySelectorAll('.gallery-bullet').forEach((dot, i) => {
          dot.classList.toggle('active', i === index);
        });

        gallery.scrollTo({
          left: slide.offsetLeft,
          behavior: 'smooth',
        });
      });
      bulletsContainer.appendChild(dot);
    });

    this.galleryDots = [...bulletsContainer.querySelectorAll('.gallery-bullet')];
  }

  updateBottomCTA() {
    const detailsButton = this.modal.querySelector('.modal-footer a');

    const { title } = this.settings.product;

    detailsButton.innerHTML = `${title} ${(window.theme && window.theme.strings && window.theme.strings.details) || 'Details'}`;
    detailsButton.href = `/products/${this.settings.product.handle}`;
  }

  assignAddToCartButton() {
    const addToCartButton = this.modal.querySelector('.modal-footer button');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', () => {
        this.addToCart(addToCartButton);
      });
    }
  }

  addToCart(addToCartButton) {
    const variantId = this.currentVariant?.id ?? this.settings.product.variants[0].id;
    if (!variantId) {
      console.error('No variant selected');
      return;
    }
    addToCartButton.disabled = true;
    addToCartButton.classList.add('disabled');
    const quantity = this.qty;

    let formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: {
            _pdp_upsell: 'true',
          },
        },
      ],
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then(response => {
        this.close();
        theme.cart._updateCart();
        return response.json();
      })
      .catch(error => {
        console.error('Error adding to cart:', request.responseText);
      })
      .finally(() => {
        addToCartButton.disabled = false;
        addToCartButton.classList.remove('disabled');
      });
  }

  iniQtySelector() {
    const qtySelectors = this.modal.querySelectorAll('.group-qty-selector__item');
    qtySelectors.forEach(qty => {
      qty.addEventListener('click', e => {
        const qtyElement = e.target;
        const qtyContainer = qtyElement.closest('.content-qty-selector');
        const qtyValue = qtyElement.getAttribute('value');

        qtyContainer.querySelectorAll('.group-qty-selector__item').forEach(opt => {
          opt.classList.remove('active');
        });

        qtyElement.classList.add('active');

        this.qty = qtyValue;
      });
    });
  }

  initGallery() {
    const gallery = this.modal.querySelector('.product-gallery');
    if (!gallery) return;
    const sliderWrapper = gallery.querySelector('.gallery-container');
    sliderWrapper.addEventListener('scroll', this.handleGalleryScroll);
  }

  handleGalleryScroll = () => {
    const gallery = this.modal.querySelector('.product-gallery .gallery-container');
    if (!gallery || !this.galleryDots?.length) return;

    const visibleSlides = [...gallery.querySelectorAll('.gallery-slide.is-visible')];
    let closestIndex = 0;
    let minDistance = Infinity;

    visibleSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - gallery.scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    this.galleryDots.forEach((dot, i) => {
      dot.classList.toggle('active', i === closestIndex);
    });
  };

  validateIsAvailable(button) {
    if (this.settings && this.settings.product && this.settings.product.available === false) {
      button.classList.add('is-disabled');
      button.setAttribute('disabled', 'disabled');
      button.innerHTML = (window.theme && window.theme.strings && window.theme.strings.outOfStock) || 'Out of stock';
    }
  }

  generateGallery() {
    let images = this.settings?.product?.images || [];
    console.log(' generateGallery!!!', images);
    if (!images || !images.length) return '';

    if (images.length === 1) {
      return `
        <div class="product-gallery">
          <div class="product-image">
            <img src="${images[0].src}" alt="${images[0].alt || ''}" loading="lazy">
          </div>
        </div>
      `;
    }

    const hasFilteredAltsValues = images.filter(image => {
      return image.alt && image.alt.includes('bundle-filtered-image:');
    });
    console.log(' hasFilteredAltsValues:', hasFilteredAltsValues);

    const slides = images
      .map((image, index) => {
        const imgSrc = typeof image === 'string' ? image : image.src || '';
        // const display = index === 0 ? "block" : "none";
        const hasSizeAlt = hasFilteredAltsValues.length && image.alt && image.alt.includes('bundle-filtered-image:');
        return `
          <div
            class="gallery-slide"
            data-index="${index}"
            ${hasSizeAlt ? `data-size="${image.alt.split('bundle-filtered-image:')[1]}"` : ''}
            style="--order: ${index + 100};"
          >
            <img src="${imgSrc}" alt="${image.alt || ''}" loading="lazy">
          </div>
        `;
      })
      .join('');

    const bullets = images
      .map(
        (_, index) => `
      <span class="gallery-bullet ${index === 0 ? 'active' : ''}" data-index="${index}"></span>
    `
      )
      .join('');

    return `
      <div class="product-gallery" id="product-gallery">
        <div class="gallery-container ${hasFilteredAltsValues.length ? 'is-filtered' : ''}">
          ${slides}
        </div>
        <div class="gallery-pagination">
          ${bullets}
        </div>
      </div>
    `;
  }

  generateVariants() {
    const product = this.settings.product || {};
    const variants = product.variants || [];

    const currentVariant = this.currentVariant || {};

    if (!variants.length) return '';

    const options = {};

    if (product.options && product.options.length) {
      product.options.forEach((option, index) => {
        options[`option${index + 1}`] = {
          name: option.name,
          values: [],
        };
      });

      variants.forEach(variant => {
        Object.keys(options).forEach(optionKey => {
          if (variant[optionKey] && !options[optionKey].values.includes(variant[optionKey])) {
            options[optionKey].values.push(variant[optionKey]);
          }
        });
      });
    }

    let variantsHTML = '';

    Object.keys(options).forEach(optionKey => {
      const option = options[optionKey];

      if (option.name === 'Title') return;

      variantsHTML += `
        <div class="product-variant-selector variant-container-for-${option.name}" data-option="${optionKey}">
          <label class="label-modal">${option.name}</label>
          <div class="variant-options">
            ${option.values
              .map(value => {
                const isCurrentVariant = currentVariant.options.includes(value);
                return `
              <span class="variant-option  ${isCurrentVariant ? 'selected' : ''}" data-value="${value}">${value}</span>
            `;
              })
              .join('')}
          </div>
        </div>
      `;
    });

    return `
      <div class="product-variants">
        ${variantsHTML}
      </div>
    `;
  }

  initVariantSelectors() {
    const variantOptions = this.modal.querySelectorAll('.variant-option');

    variantOptions.forEach(option => {
      option.addEventListener('click', e => {
        const optionElement = e.target;
        const optionContainer = optionElement.closest('.product-variant-selector');
        const optionKey = optionContainer.getAttribute('data-option');
        const optionValue = optionElement.getAttribute('data-value');

        optionContainer.querySelectorAll('.variant-option').forEach(opt => {
          opt.classList.remove('selected');
        });

        optionElement.classList.add('selected');

        this.selectedOptions = this.selectedOptions || {};
        this.selectedOptions[optionKey] = optionValue;

        this.updateSelectedVariant();
        this.updateProductInfo(this.currentVariant);
        this.filterGalleryBySize(this.currentVariant);
      });
    });
  }

  updateSelectedVariant() {
    const variants = this.settings.product.variants || [];

    const selectedVariant = variants.find(variant => {
      return Object.keys(this.selectedOptions).every(optionKey => {
        return variant[optionKey] === this.selectedOptions[optionKey];
      });
    });

    if (!selectedVariant) return;

    this.currentVariant = selectedVariant;
  }

  updateProductInfo(variant) {
    this.updateProductPrice(variant);
  }

  updateProductPrice(variant) {
    const priceElement = this.modal.querySelector('.product-price, .product-single__prices');
    if (priceElement && variant.price) {
      priceElement.innerHTML = `
        <div class="product-single__prices">
          <span class="visually-hidden" aria-hidden="false">${(window.theme && window.theme.strings && window.theme.strings.regularPrice) || 'Regular price'}</span>

          <span class="product__price product__price--compare">
            ${formatMoney(variant.price, (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}')}
          </span>

          <span class="visually-hidden">${(window.theme && window.theme.strings && window.theme.strings.salePrice) || 'Sale price'}</span>
          <span class="product__price">
            ${formatMoney(variant.price - 1000, (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}')}
          </span>
        </div>
      `;
    }
  }

  generateQtySelector() {
    return `
      <div class="content-qty-selector flex flex-col items-center">
        <label for="qty" class="mb-4 label-modal">${(window.theme && window.theme.strings && window.theme.strings.quantity) || 'Quantity'}</label>
        <div class="group-qty-selector">
          ${this.QTY_SELECTOR.map((qty, index) => {
            return `<span value=${qty} class="group-qty-selector__item qty ${index == 0 ? 'active' : ''}">${qty}</span>`;
          }).join('')}
        </div>
      </div>
    `;
  }

  open() {
    this.modal.classList.add('open');
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  handleOutsideClick(event) {
    if (event.target.closest('.cs-modal-content')) return;
    this.close();
  }

  close() {
    document.documentElement.classList.remove('js-drawer-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.height = '';

    this.modal.classList.remove('open');
    this.modal.remove();
  }
}

// Add listener for dynamic content (for when section is loaded via AJAX)
document.addEventListener('shopify:section:load', event => {
  const container = event.target.querySelector('[id^="UpsellContainer-"]');
  if (container) {
    new UpsellBlock(container);
  }
});
